const KNOWN_QUOTES = [
  "USDT",
  "USDC",
  "FDUSD",
  "BUSD",
  "TUSD",
  "USD",
  "BTC",
  "ETH",
  "BNB",
  "EUR",
  "TRY",
];

const KNOWN_ICON_NAMES = new Set(["btc", "eth", "bnb", "xrp", "sol", "usdt"]);
const BLOCKED_TRADE_SYMBOLS = new Set(["dogecoinusdt", "tronusdt"]);

const clampPrecision = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(18, Math.floor(num)));
};

export const normalizeSymbolCode = (value) =>
  String(value ?? "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();

export const getTradeItemSymbol = (item) =>
  String(item?.symbol ?? item?.name ?? "");

export const splitTradeSymbol = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return { base: "", quote: "", normalized: "" };

  if (raw.includes("/")) {
    const [left = "", right = ""] = raw.split("/");
    const base = left.toUpperCase();
    const quote = right.toUpperCase();
    return {
      base,
      quote,
      normalized: normalizeSymbolCode(`${base}${quote}`),
    };
  }

  const upper = normalizeSymbolCode(raw).toUpperCase();
  if (!upper) return { base: "", quote: "", normalized: "" };

  for (const quote of KNOWN_QUOTES) {
    if (upper.endsWith(quote) && upper.length > quote.length) {
      const base = upper.slice(0, -quote.length);
      return {
        base,
        quote,
        normalized: normalizeSymbolCode(upper),
      };
    }
  }

  if (upper.length > 3) {
    return {
      base: upper.slice(0, upper.length - 3),
      quote: upper.slice(-3),
      normalized: normalizeSymbolCode(upper),
    };
  }

  return { base: upper, quote: "", normalized: normalizeSymbolCode(upper) };
};

export const formatTradeSymbol = (value) => {
  const { base, quote } = splitTradeSymbol(value);
  if (base && quote) return `${base}/${quote}`;
  return base || quote || "";
};

export const getTradeSymbolIcons = (value) => {
  const { base, quote } = splitTradeSymbol(value);
  const baseIcon = base.toLowerCase();
  const quoteIcon = quote.toLowerCase();
  const icons = [];

  if (baseIcon) {
    icons.push(baseIcon);
  }
  if (quoteIcon) {
    icons.push(quoteIcon);
  }

  return icons;
};

export const isLocalTradeIcon = (value) =>
  KNOWN_ICON_NAMES.has(normalizeSymbolCode(value));

export const getTradeIconUrls = (value) => {
  const symbol = normalizeSymbolCode(value);
  if (!symbol) return [];

  return [
    `https://cryptoicons.org/api/color/${symbol}/200`,
    `https://cdn.jsdelivr.net/gh/spothq/cryptocurrency-icons@master/32/color/${symbol}.png`,
    `https://farisaziz12.github.io/cryptoicon-api/icons/${symbol}.png`,
  ];
};

const countFractionDigits = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const raw = String(value).trim();
  if (!raw) return null;

  const normalized = raw.toLowerCase();

  if (normalized.includes("e")) {
    const num = Number(normalized);
    if (!Number.isFinite(num)) return null;
    const fixed = num
      .toFixed(18)
      .replace(/\.?0+$/, "")
      .replace("-0", "0");
    const dot = fixed.indexOf(".");
    return dot === -1 ? 0 : fixed.length - dot - 1;
  }

  const dot = normalized.indexOf(".");
  if (dot === -1) return 0;
  return normalized.slice(dot + 1).replace(/0+$/, "").length;
};

const readIntegerPrecisionField = (source, fieldName) => {
  const value = source?.[fieldName];
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return clampPrecision(parsed);
};

const readStepPrecisionField = (source, fieldName) => {
  const value = source?.[fieldName];
  const places = countFractionDigits(value);
  if (places === null) return null;
  return clampPrecision(places);
};

export const inferSymbolPricePrecision = (symbolData, fallback = 4) => {
  if (!symbolData || typeof symbolData !== "object") {
    return clampPrecision(fallback);
  }

  const candidates = [];

  [
    "price_precision",
    "pricePrecision",
    "quote_precision",
    "quotePrecision",
    "price_decimals",
    "priceDecimals",
    "decimals",
  ].forEach((field) => {
    const precision = readIntegerPrecisionField(symbolData, field);
    if (precision !== null) candidates.push(precision);
  });

  [
    "tick_size",
    "tickSize",
    "price_tick",
    "priceTick",
    "price_step",
    "priceStep",
    "step_size",
    "stepSize",
    "price_increment",
    "priceIncrement",
    "quote_increment",
    "quoteIncrement",
    "min_price_step",
    "minPriceStep",
  ].forEach((field) => {
    const precision = readStepPrecisionField(symbolData, field);
    if (precision !== null) candidates.push(precision);
  });

  const bidPrices =
    Array.isArray(symbolData?.book?.bids) && symbolData.book.bids.length
      ? symbolData.book.bids.map((item) => item?.price)
      : [];
  const askPrices =
    Array.isArray(symbolData?.book?.asks) && symbolData.book.asks.length
      ? symbolData.book.asks.map((item) => item?.price)
      : [];

  [symbolData?.last, ...bidPrices, ...askPrices].forEach((price) => {
    const precision = countFractionDigits(price);
    if (precision !== null) candidates.push(clampPrecision(precision));
  });

  if (!candidates.length) return clampPrecision(fallback);
  return Math.max(...candidates);
};

export const findSymbolData = (symbols = [], symbolCode) => {
  const target = normalizeSymbolCode(symbolCode);
  if (!target || !Array.isArray(symbols)) return null;
  return (
    symbols.find(
      (item) => normalizeSymbolCode(getTradeItemSymbol(item)) === target,
    ) || null
  );
};

const getFirstBookPrice = (rows) => {
  if (!Array.isArray(rows)) return null;

  for (const row of rows) {
    const price = Number(row?.price);
    if (Number.isFinite(price)) return price;
  }

  return null;
};

export const resolveSideMarketPrice = (symbolData, side) => {
  const direction = getTradeSideDirection(side);
  const bookSide =
    direction === 1 ? symbolData?.book?.asks : symbolData?.book?.bids;
  const bookPrice = getFirstBookPrice(bookSide);

  if (bookPrice !== null) return bookPrice;

  const fallbackPrice = Number(symbolData?.last);
  return Number.isFinite(fallbackPrice) ? fallbackPrice : null;
};

export const TP_SL_VALIDATION_MESSAGES = {
  marketUnavailable:
    "قیمت بازار در دسترس نیست. لطفاً چند لحظه بعد دوباره تلاش کنید.",
  buyTp: "حد سود برای معامله خرید باید بالاتر از قیمت بازار باشد.",
  buySl: "حد ضرر برای معامله خرید باید پایین‌تر از قیمت بازار باشد.",
  sellTp: "حد سود برای معامله فروش باید پایین‌تر از قیمت بازار باشد.",
  sellSl: "حد ضرر برای معامله فروش باید بالاتر از قیمت بازار باشد.",
};

export const getTpSlValidationMessages = ({
  side,
  marketPrice,
  tpPrice,
  slPrice,
}) => {
  const hasMarketPrice = Number.isFinite(Number(marketPrice));
  const direction = getTradeSideDirection(side);
  const messages = { tp: "", sl: "" };

  if (!hasMarketPrice) {
    if (tpPrice) messages.tp = TP_SL_VALIDATION_MESSAGES.marketUnavailable;
    if (slPrice) messages.sl = TP_SL_VALIDATION_MESSAGES.marketUnavailable;
    return messages;
  }

  const currentPrice = Number(marketPrice);

  if (tpPrice) {
    const price = Number(tpPrice);
    if (direction === 1 && price <= currentPrice) {
      messages.tp = TP_SL_VALIDATION_MESSAGES.buyTp;
    } else if (direction === -1 && price >= currentPrice) {
      messages.tp = TP_SL_VALIDATION_MESSAGES.sellTp;
    }
  }

  if (slPrice) {
    const price = Number(slPrice);
    if (direction === 1 && price >= currentPrice) {
      messages.sl = TP_SL_VALIDATION_MESSAGES.buySl;
    } else if (direction === -1 && price <= currentPrice) {
      messages.sl = TP_SL_VALIDATION_MESSAGES.sellSl;
    }
  }

  return messages;
};

export const formatTrimmedNumber = (
  value,
  { useGrouping = false, fallback = "-", maxFractionDigits = 18 } = {},
) => {
  if (value === null || value === undefined || value === "") return fallback;

  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;

  return num.toLocaleString("en-US", {
    useGrouping,
    maximumFractionDigits: clampPrecision(maxFractionDigits),
  });
};

const toFiniteTradeNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const normalized = String(value).trim();
  if (!normalized || normalized === ".") return null;

  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
};

const getTradeSideDirection = (side) => {
  const normalized = String(side ?? "")
    .trim()
    .toLowerCase();

  if (normalized === "buy" || normalized === "long") return 1;
  if (normalized === "sell" || normalized === "short") return -1;
  return null;
};

export const resolveTradeEntryPrice = ({
  orderType,
  marketPrice,
  limitPrice,
  stopPrice,
}) => {
  const normalizedOrderType = String(orderType ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_-\s]/g, "");

  if (normalizedOrderType === "market") {
    return toFiniteTradeNumber(marketPrice);
  }

  if (normalizedOrderType === "limit" || normalizedOrderType === "stoplimit") {
    return toFiniteTradeNumber(limitPrice);
  }

  if (normalizedOrderType === "stopmarket") {
    return toFiniteTradeNumber(stopPrice);
  }

  return null;
};

export const calculateEstimatedTradePnlDetails = ({
  side,
  entryPrice,
  targetPrice,
  marginUsdt,
  leverage,
}) => {
  const direction = getTradeSideDirection(side);
  const safeEntryPrice = toFiniteTradeNumber(entryPrice);
  const safeTargetPrice = toFiniteTradeNumber(targetPrice);
  const safeMarginUsdt = toFiniteTradeNumber(marginUsdt);
  const safeLeverage = toFiniteTradeNumber(leverage);

  if (
    direction === null ||
    safeEntryPrice === null ||
    safeTargetPrice === null ||
    safeMarginUsdt === null ||
    safeLeverage === null ||
    safeEntryPrice <= 0 ||
    safeMarginUsdt <= 0 ||
    safeLeverage <= 0
  ) {
    return null;
  }

  const positionSize = (safeMarginUsdt * safeLeverage) / safeEntryPrice;

  if (!Number.isFinite(positionSize) || positionSize <= 0) {
    return null;
  }

  const rawPnl = (safeTargetPrice - safeEntryPrice) * positionSize * direction;

  if (!Number.isFinite(rawPnl)) {
    return null;
  }

  const maxLoss = -safeMarginUsdt;
  const liquidationTolerance = Math.max(
    Number.EPSILON * Math.max(1, Math.abs(safeMarginUsdt)) * 100,
    1e-9,
  );
  const liquidated =
    rawPnl <= maxLoss || Math.abs(rawPnl - maxLoss) <= liquidationTolerance;

  return {
    pnl: liquidated ? maxLoss : rawPnl,
    liquidated,
  };
};

export const calculateEstimatedTradePnl = (params) => {
  const details = calculateEstimatedTradePnlDetails(params);
  return details?.pnl ?? null;
};

export const getEstimatedTradePnlMeta = (
  value,
  { liquidated = false } = {},
) => {
  const pnl = toFiniteTradeNumber(value);
  if (pnl === null) return null;

  const isProfit = pnl >= 0;

  return {
    isProfit,
    label: isProfit ? "سود تخمینی" : "ضرر تخمینی",
    suffix: liquidated ? " (لیکویید)" : "",
    tone: isProfit ? "profit" : "loss",
  };
};

export const isValidDecimalInput = (value, maxDecimals) => {
  if (value === "") return true;

  const decimals = clampPrecision(maxDecimals);
  if (decimals === 0) return /^\d*$/.test(value);

  const pattern = new RegExp(`^\\d*\\.?\\d{0,${decimals}}$`);
  return pattern.test(value);
};

export const isBlockedTradeSymbol = (value) =>
  BLOCKED_TRADE_SYMBOLS.has(normalizeSymbolCode(value));

const filterSymbolItems = (items = []) =>
  items.filter((item) => !isBlockedTradeSymbol(getTradeItemSymbol(item)));

export const normalizeMarketSymbolData = (item) => {
  if (!item || typeof item !== "object") return null;

  const symbol = normalizeSymbolCode(getTradeItemSymbol(item));
  if (!symbol || isBlockedTradeSymbol(symbol)) return null;

  const bookBids = Array.isArray(item?.book?.bids)
    ? item.book.bids
    : Array.isArray(item?.bids)
      ? item.bids
      : [];
  const bookAsks = Array.isArray(item?.book?.asks)
    ? item.book.asks
    : Array.isArray(item?.asks)
      ? item.asks
      : [];

  return {
    ...item,
    symbol,
    book: {
      bids: bookBids,
      asks: bookAsks,
    },
  };
};

export const sanitizeMarketSymbols = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => normalizeMarketSymbolData(item))
    .filter(Boolean);

export const mergeMarketSymbolUpdate = (symbols = [], nextItem) => {
  const normalizedSymbols = sanitizeMarketSymbols(symbols);
  const normalizedItem = normalizeMarketSymbolData(nextItem);

  if (!normalizedItem) return normalizedSymbols;

  const nextIndex = normalizedSymbols.findIndex(
    (item) => item.symbol === normalizedItem.symbol,
  );

  if (nextIndex === -1) {
    return [...normalizedSymbols, normalizedItem];
  }

  return normalizedSymbols.map((item, index) =>
    index === nextIndex
      ? {
          ...item,
          ...normalizedItem,
          book: normalizedItem.book ?? item.book,
        }
      : item,
  );
};

export const sanitizeTradeAccountSnapshot = (data) => {
  if (!data || typeof data !== "object") return null;

  return {
    account:
      data.account && typeof data.account === "object"
        ? { ...data.account }
        : null,
    orders: {
      ...(data.orders && typeof data.orders === "object" ? data.orders : {}),
      open: Array.isArray(data?.orders?.open)
        ? filterSymbolItems(data.orders.open)
        : [],
    },
    positions: {
      ...(data.positions && typeof data.positions === "object"
        ? data.positions
        : {}),
      open: Array.isArray(data?.positions?.open)
        ? filterSymbolItems(data.positions.open)
        : [],
    },
  };
};

export const applyTradeAccountMark = (state, payload) => {
  if (!state || typeof state !== "object") return state;

  const nextState = { ...state };

  if (payload?.account && typeof payload.account === "object") {
    nextState.account = {
      ...(state.account && typeof state.account === "object"
        ? state.account
        : {}),
    };

    if (payload.account.equity !== undefined) {
      nextState.account.equity = payload.account.equity;
    }
    if (payload.account.unrealized_pnl !== undefined) {
      nextState.account.unrealized_pnl = payload.account.unrealized_pnl;
    }
  }

  if (Array.isArray(state?.positions?.open)) {
    const pnlById = new Map(
      (Array.isArray(payload?.positions) ? payload.positions : [])
        .filter((item) => item?.id !== undefined && item?.id !== null)
        .map((item) => [String(item.id), item.unrealized_pnl]),
    );

    nextState.positions = {
      ...(state.positions && typeof state.positions === "object"
        ? state.positions
        : {}),
      open: state.positions.open.map((position) => {
        const positionId = String(position?.id ?? "");
        if (!pnlById.has(positionId)) return position;

        return {
          ...position,
          unrealized_pnl: pnlById.get(positionId),
        };
      }),
    };
  }

  return nextState;
};

export const sanitizeTradeData = (data) => {
  if (!data || typeof data !== "object") return data;

  const nextData = sanitizeTradeAccountSnapshot(data) || {};

  if (data.market && typeof data.market === "object") {
    nextData.market = {
      ...data.market,
      symbols: sanitizeMarketSymbols(data.market.symbols),
    };
  }

  return nextData;
};
